import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Button } from '@material-ui/core';
import ModalPage from 'components/explore/layout/ModalPage';
import UserMultiSelect from 'components/explore/views/UserMultiSelect';
import { updateExploreViewSharing } from 'store/actions';
import { sendClientLog } from 'services/logService';

// Whether to show the Share View menu option (View panel and ViewCard menus), and why it's disabled, if it is
export const getShareOptionState = (view, user, isDirty) => {
  const isTemp = view?.id === 'temp';
  const show = view?.id !== 'system'
    && view?.privacy === 'Private'
    && (isTemp || view?.createdByUserId === user.id || user.roles.administrator);
  const disabledReason = isTemp
    ? 'Save this view before sharing it'
    : isDirty
      ? 'Save or discard your changes before sharing'
      : null;
  return { show, disabledReason };
}

// Owner/admin-only: choose who a Private view is shared with. Shared users can open and apply the view
// (and Duplicate it), but only the owner or an admin can save, delete or re-share it.
function ShareViewModal({ classes, view, modalOpen, onClose, isMobile, updateExploreViewSharing }) {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (modalOpen) {
      setSelectedUserIds((view?.sharedWithUsers ?? []).map(user => user.id));
      setIsSaving(false);
    }
  }, [modalOpen, view?.id]);

  const handleSave = () => {
    setIsSaving(true);
    sendClientLog('Info', `Explore Views - User saved sharing for view id ${view.id} (${selectedUserIds.length} users)`);
    updateExploreViewSharing(view.id, selectedUserIds, succeeded => succeeded ? onClose() : setIsSaving(false));
  }

  if (!view) return null;
  return (
    <ModalPage
      maxWidth='sm'
      fullScreen={isMobile}
      title={`Share "${view.name}"`}
      open={modalOpen}
      onClose={onClose}
    >
      <div className={classes.form}>
        <Typography variant='body2' className={classes.description}>
          Shared users can open this view and duplicate it to make their own copy. Only you or an admin can modify or delete the original.
        </Typography>
        <UserMultiSelect
          label='Share with'
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          excludeUserIds={[view.createdByUserId]}
          disabled={isSaving}
        />
        <div className={classes.actions}>
          <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button variant='contained' color='primary' onClick={handleSave} disabled={isSaving}>Save</Button>
        </div>
      </div>
    </ModalPage>
  );
}

const styles = () => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    paddingBottom: 4
  },
  description: {
    color: 'rgba(0, 0, 0, 0.6)'
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8
  }
});

const mapState = ({ appState }) => ({
  isMobile: appState.isMobile
});

const mapDispatch = {
  updateExploreViewSharing
};

export default compose(withStyles(styles), connect(mapState, mapDispatch))(ShareViewModal);
